import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProperties } from "@/lib/api";
import { Bed, Bath, Maximize, MapPin, ArrowUpRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const fmtAED = (n) => `AED ${(n / 1_000_000).toFixed(1)}M`;
const fmtUSD = (n) => `USD ${(n / 1_000_000).toFixed(1)}M`;

export default function Listings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties({ featured: true, limit: 4 })
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      id="listings"
      data-testid="listings-section"
      className="py-24 lg:py-36 bg-[#f5f0e8] border-t border-[#1c1c1c]/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-16">
          <SectionHeading
            title={<>Private residences,<br /><em className="not-italic text-[#3d2b1f]">selected by hand</em>.</>}
            subtitle="The Collection · Featured · 2026"
          />
          <Link
            to="/listings"
            data-testid="listings-view-all"
            className="self-start lg:self-end inline-flex items-center gap-3 px-7 py-3 border border-[#c5a059]/60 text-[#c5a059] uppercase text-[10px] tracking-[0.32em] hover:bg-[#c5a059] hover:text-[#1c1c1c] transition-colors duration-500"
          >
            View Full Collection <ArrowUpRight size={14} />
          </Link>
        </div>

        {loading && (
          <div className="text-[#3d2b1f] text-sm" data-testid="listings-loading">
            Curating the collection…
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 lg:gap-x-8 gap-y-16">
          {items.map((p, i) => (
            <Link
              key={p.id}
              to={`/listings/${p.slug}`}
              data-testid={`listing-card-${p.slug}`}
              className="group block"
            >
              <div className="img-zoom aspect-[4/5] mb-6 bg-[#ede5d5]">
                <img
                  src={p.images[0]}
                  alt={p.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <span className="underline-label mb-2 text-[#c5a059]">{p.type} · {p.status}</span>
              <h3 className="mt-3 font-display text-xl lg:text-2xl text-[#1c1c1c] group-hover:text-[#3d2b1f] transition-colors">
                {p.title}
              </h3>
              <div className="mt-2 flex items-center gap-2 text-[#3d2b1f] text-xs">
                <MapPin size={12} className="text-[#c5a059]" />
                {p.location}
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <div className="font-display text-lg text-[#1c1c1c]">{fmtAED(p.price_aed)}</div>
                <div className="text-[9px] tracking-[0.28em] text-[#3d2b1f]">{fmtUSD(p.price_usd)}</div>
              </div>
              <div className="mt-4 flex items-center gap-4 text-[11px] uppercase tracking-[0.2em] text-[#3d2b1f]">
                <span className="flex items-center gap-1.5"><Bed size={12} className="text-[#c5a059]" /> {p.bedrooms}</span>
                <span className="flex items-center gap-1.5"><Bath size={12} className="text-[#c5a059]" /> {p.bathrooms}</span>
                <span className="flex items-center gap-1.5"><Maximize size={12} className="text-[#c5a059]" /> {p.size_sqft.toLocaleString()}</span>
              </div>
              <div className="mt-5 h-px bg-[#1c1c1c]/10 group-hover:bg-[#c5a059] transition-colors" />
              <span className="underline-label mt-4">Enquire — 0{i + 1}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
